import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import RegisImg from "../assets/RegisImg.jpg";
import { registerUser } from '../../redux/slices/authSlice';
import { mergeCart } from '../../redux/slices/cartSlice';

const Register = () => {
  const [name,         setName]         = useState('');
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, guestId, loading, error } = useSelector((state) => state.auth);
  const { cart }                           = useSelector((state) => state.cart);

  const redirect           = new URLSearchParams(location.search).get('redirect') || '/';
  const isCheckoutRedirect = redirect.includes('checkout');

  // Redirect after successful registration
  useEffect(() => {
    if (user) {
      toast.dismiss();
      toast.success('Welcome to VESHH-EON!');
      if (cart?.products?.length > 0 && guestId) {
        dispatch(mergeCart({ guestId, user })).then(() => {
          navigate(isCheckoutRedirect ? '/checkout' : '/');
        });
      } else {
        navigate(isCheckoutRedirect ? '/checkout' : '/');
      }
    }
  }, [user, guestId, cart, navigate, isCheckoutRedirect, dispatch]);

  // Error toast
  useEffect(() => {
    if (error) {
      toast.dismiss();
      toast.error(error || 'Registration failed. Please try again.');
    }
  }, [error]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name.trim() || !email.trim() || !password.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const loadingId = toast.loading('Creating your account...');
    dispatch(registerUser({ name, email, password })).finally(() => {
      toast.dismiss(loadingId);
    });
  };

  return (
    <>
      <style>{registerStyles}</style>

      <div className="rg-root">

        {/* ── LEFT — form ── */}
        <div className="rg-left">
          <div className="rg-form-wrap">

            {/* Logo */}
            <div className="rg-logo-wrap">
              <img
                src="https://res.cloudinary.com/dmrlotfsv/image/upload/v1754071341/VESHH-EON_TYPE_WEB_ppfyxy.png"
                alt="VESHH-EON"
                className="rg-logo"
              />
            </div>

            {/* Header */}
            <div className="rg-heading-wrap">
              <span className="rg-eyebrow">New Here?</span>
              <h2 className="rg-heading">
                Create <span className="rg-heading-bold">Account</span>
              </h2>
            </div>

            <div className="rg-rule"><hr className="rg-hr" /></div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="rg-form">

              <div className="rg-field">
                <label className="rg-label" htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="rg-input"
                />
              </div>

              <div className="rg-field">
                <label className="rg-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@veshheon.bharat"
                  required
                  className="rg-input"
                />
              </div>

              <div className="rg-field">
                <label className="rg-label" htmlFor="password">Password</label>
                <div className="rg-password-wrap">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    required
                    minLength={6}
                    className="rg-input rg-input--password"
                  />
                  <button
                    type="button"
                    className="rg-eye-btn"
                    onClick={() => setShowPassword(p => !p)}
                    tabIndex={-1}
                  >
                    {showPassword
                      ? <EyeOff size={15} strokeWidth={1.75} />
                      : <Eye    size={15} strokeWidth={1.75} />
                    }
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="rg-submit"
              >
                {loading ? (
                  <span className="rg-submit-loading">
                    <Loader2 size={13} className="rg-spinner" />
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>

            </form>

            {/* Login link */}
            <p className="rg-login">
              Already have an account?{' '}
              <Link
                to={`/login?redirect=${encodeURIComponent(redirect)}`}
                className="rg-login-link"
              >
                Sign In
              </Link>
            </p>

            {/* Legal */}
            <p className="rg-legal">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="rg-legal-link">Terms</Link>
              {' '}and{' '}
              <Link to="/privacypolicy" className="rg-legal-link">Privacy Policy</Link>
            </p>

          </div>
        </div>

        {/* ── RIGHT — image ── */}
        <div className="rg-right">
          <div className="rg-right-overlay" />
          <img src="https://res.cloudinary.com/dmrlotfsv/image/upload/t_VESHH-EON_WEBSITE/Register_mcavaq.png" alt="VESHH-EON" className="rg-right-img" />
          <div className="rg-right-copy">
            <span className="rg-right-eyebrow">Begin Here</span>
            <h3 className="rg-right-heading">
              Don't Just Exist —<br />
              <span className="rg-right-bold">Be The Author.</span>
            </h3>
            <p className="rg-right-sub">
              Walk like you own the damn street.
            </p>
          </div>
        </div>

      </div>
    </>
  );
};

const registerStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  @keyframes rg-spin { to { transform: rotate(360deg); } }

  .rg-root {
    display: flex;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
  }

  /* ── LEFT ── */
  .rg-left {
    width: 100%;
    background: #f8f8f8;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
  }
  @media (min-width: 768px) {
    .rg-left { width: 50%; padding: 64px 48px; }
  }

  .rg-form-wrap {
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
  }

  /* Logo */
  .rg-logo-wrap {
    display: flex;
    justify-content: center;
    margin-bottom: 36px;
  }
  .rg-logo {
    height: 36px;
    width: auto;
    object-fit: contain;
  }

  /* Heading */
  .rg-heading-wrap { margin-bottom: 24px; }

  .rg-eyebrow {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: black;
    margin-bottom: 10px;
  }
  .rg-eyebrow::before {
    content: '';
    width: 20px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .rg-heading {
    font-size: clamp(28px, 4vw, 44px);
    font-weight: 200;
    color: #000;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1;
    margin: 0;
  }
  .rg-heading-bold { font-weight: 600; }

  /* Rule */
  .rg-rule { margin-bottom: 28px; }
  .rg-hr {
    border: none;
    border-top: 1px solid black;
    margin: 0;
  }

  /* Form */
  .rg-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .rg-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rg-label {
    font-size: 8px;
    font-weight: 500;
    letter-spacing: 0.45em;
    text-transform: uppercase;
    color: black;
  }

  .rg-input {
    background: transparent;
    border: none;
    border-bottom: 1px solid black;
    outline: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 13px;
    font-weight: 300;
    letter-spacing: 0.06em;
    color: #000;
    padding: 10px 0;
    width: 100%;
    transition: border-color 0.2s ease;
    border-radius: 0;
    -webkit-appearance: none;
  }
  .rg-input::placeholder { color: black; }
  .rg-input:focus { border-bottom-color: #000; }
  .rg-input--password { padding-right: 28px; }

  .rg-password-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  .rg-eye-btn {
    position: absolute;
    right: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    color: black;
    display: flex;
    align-items: center;
    padding: 0;
    transition: color 0.15s;
  }
  .rg-eye-btn:hover { color: #000; }

  /* Submit */
  .rg-submit {
    width: 100%;
    background: #000;
    color: #fff;
    border: none;
    font-family: 'Exo 2', sans-serif;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    padding: 16px;
    cursor: pointer;
    transition: background 0.2s ease;
    margin-top: 8px;
  }
  .rg-submit:hover:not(:disabled) { background: #222; }
  .rg-submit:disabled { background: rgba(0,0,0,0.2); cursor: not-allowed; }

  .rg-submit-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .rg-spinner { animation: rg-spin 1s linear infinite; }

  /* Login link */
  .rg-login {
    margin-top: 24px;
    text-align: center;
    font-size: 10px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
  }

  .rg-login-link {
    font-weight: 600;
    color: #000;
    text-decoration: none;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    font-size: 9px;
    position: relative;
    padding-bottom: 2px;
  }
  .rg-login-link::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0;
    width: 100%; height: 1px;
    background: black;
    transition: background 0.2s;
  }
  .rg-login-link:hover::after { background: #000; }

  /* Legal */
  .rg-legal {
    margin-top: 16px;
    text-align: center;
    font-size: 8px;
    font-weight: 300;
    letter-spacing: 0.15em;
    color: black;
    text-transform: uppercase;
  }

  .rg-legal-link {
    color: black;
    text-decoration: underline;
    transition: color 0.2s;
  }
  .rg-legal-link:hover { color: #000; }

  /* ── RIGHT ── */
  .rg-right {
    display: none;
    position: relative;
    overflow: hidden;
    background: #000;
  }
  @media (min-width: 768px) {
    .rg-right { display: flex; width: 50%; }
  }

  .rg-right-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 50%, transparent 100%);
    z-index: 1;
  }

  .rg-right-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    display: block;
  }

  .rg-right-copy {
    position: absolute;
    bottom: 48px;
    left: 48px;
    right: 48px;
    z-index: 2;
  }

  .rg-right-eyebrow {
    display: block;
    font-size: 8px;
    font-weight: 400;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin-bottom: 14px;
  }

  .rg-right-heading {
    font-size: clamp(24px, 3vw, 40px);
    font-weight: 200;
    color: #fff;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.15;
    margin: 0 0 12px;
  }
  .rg-right-bold { font-weight: 600; }

  .rg-right-sub {
    font-size: 11px;
    font-weight: 300;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.4);
    margin: 0;
  }
`;

export default Register;
